/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import moment from "moment";
import { IntlShape, FormattedMessage } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Column } from "react-table";

import { ReactComponent as CheckmarkCircle } from "assets/images/icons/CheckmarkCircle.svg";
import Badge from "components/atoms/Badge/Badge";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import { formatDatetime, formatWeight, roundTo } from "helpers/commonHelpers";
import Cookies from "js-cookie";
import { QuoteStatusColors } from "constants/";
import { DateTimeFormat } from "helpers/dateTimeHelper";
import { getLabelWithTranslationFallback } from "helpers/getLabel";

function formatDate(date: any) {
  // handle case when user does not select any date from pickup or delivery date and return -
  if (date === " ") return " -";
  if (date === "ASAP") return "ASAP";

  // handle draft save for pickup date and delivery date
  if (date && date !== " ") {
    const splitDate = date.split(" ");
    // after split index 0 and index 1 if same date and return ASAP
    if (splitDate[0] === splitDate[1]) return "ASAP";
  }
  return moment(date).format("DD MMM YYYY");
}

function formatDatetimeWithLocale(datetime: any, intl: IntlShape, customFormat = DateTimeFormat.shortDateTimeFormat) {
  if (datetime === null || datetime === " ") return "-";
  if (datetime?.includes("ASAP")) {
    return intl.formatMessage({
      id: "new-request.schedule.defaultCargoReadyOption.asap",
      defaultMessage: "ASAP",
    });
  }
  return moment(datetime).format(customFormat);
}

const getQuoteListingColumns = (intl: IntlShape, status: string) => {
  const columns: Column[] = [
    {
      id: "shipment",
      Header: intl.formatMessage({
        id: status === "draft" ? "general.draft" : "general.shipment",
        defaultMessage: status === "draft" ? "Draft" : "Shipment",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "quote_request_reference",
      sortable: true,
      Cell: ({ value, row }: any) => (
        <>
          <p className="font-b4 mb-1">{value || "-"}</p>
          <span>{row?.original?.quote_request_title || "-"}</span>
        </>
      ),
    },
    {
      id: "quotes_count",
      Header: intl.formatMessage({
        id: "quote.listing.quotations",
        defaultMessage: "Quotations",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "quotes_count",
      sortable: true,
      Cell: ({ value }: any) => <>{value}</>,
    },
    {
      id: "shipper",
      Header: intl.formatMessage({
        id: "general.shipper",
        defaultMessage: "Shipper",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "shipper",
      sortable: true,
      Cell: ({ value, row }: any) => (
        <>
          <div>{row?.original?.payment_location}</div>
          <div>{value}</div>
        </>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "shipments.freightModeTableHeader",
        defaultMessage: "Freight mode",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "freight_modes_v2",
      Cell: ({ value }) => (
        <div>
          {value?.length > 0
            ? value.map((item: any, index: number) => {
                const Icon = getFreightModeIcon(item.name, item.type, true);
                return (
                  <span className="mr-2" key={index}>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="freight-mode">
                          <span className="font-weight-normal">{item.name}</span>
                        </Tooltip>
                      }
                    >
                      <Icon />
                    </OverlayTrigger>
                  </span>
                );
              })
            : "-"}
        </div>
      ),
    },
    {
      id: "route",
      Header: intl.formatMessage({
        id: "quote.listing.fromTo",
        defaultMessage: "From-to",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "pickup_city",
      sortable: true,
      Cell: ({ value, row }: any) => {
        if (!value && !row.original?.stop1_city) return <div className="mb-0">-</div>;
        const deliveryDate = row.original?.stop1_date;
        const pickupDate = row.original?.pickup_date;
        const pickupCity = row.original?.pickup_city;
        const deliveryCity = row.original?.stop1_city;
        return (
          <>
            <div>
              <div className="d-flex justify-content-between w-190-px">
                <div className="font-b5 overflow-hidden text-truncate text-secondary w-90-px">
                  <span>{pickupCity}</span>
                  <div className="text-primary text-truncate">
                    {Array.isArray(pickupDate) ? (
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 50, hide: 50 }}
                        overlay={
                          <Tooltip id="button-tooltip">
                            {formatDatetime(pickupDate[0])} - {formatDatetime(pickupDate[1])}
                          </Tooltip>
                        }
                      >
                        <span>
                          {formatDatetime(pickupDate[0])} - {formatDatetime(pickupDate[1])}
                        </span>
                      </OverlayTrigger>
                    ) : (
                      formatDatetime(pickupDate)
                    )}
                  </div>
                </div>
                <div className="font-b5 text-right overflow-hidden text-truncate text-secondary w-90-px">
                  <span>{deliveryCity}</span>
                  <div className="font-b5 text-primary">
                    {formatDatetimeWithLocale(deliveryDate, intl, DateTimeFormat.shortDateTimeFormat)}
                  </div>
                </div>
              </div>
            </div>
            {Cookies.get("listing_sizeperpage") !== "100" && (
              <div className="d-flex justify-content-between position-relative w-190-px">
                <div
                  style={{
                    width: 190,
                    height: 2,
                    borderBottom: `2px dotted black`,
                    position: "absolute",
                    top: 3,
                  }}
                />
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "2px solid black",
                      zIndex: 10,
                    }}
                  />
                </div>

                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "2px solid black",
                      zIndex: 10,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        );
      },
    },
    {
      id: "status",
      Header: intl.formatMessage({
        id: "general.status",
        defaultMessage: "Status",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "display_status",
      sortable: true,
      Cell: ({ value, row }: any) => {
        const timeRemaning = row.original?.quoting_deadline_remaining_time_display;
        let badgeType = QuoteStatusColors[value] || "secondary";
        if (value === "Open for quotations") {
          const remainingTimeInSeconds = row.original?.quoting_deadline_remaining_seconds;
          if (remainingTimeInSeconds > 0 && remainingTimeInSeconds < 300) {
            badgeType = "danger";
          } else if (remainingTimeInSeconds > 0 && remainingTimeInSeconds < 3600) {
            badgeType = "warning";
          }
        }
        return (
          <div className="d-flex flex-column align-items-start">
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center">{value && <Badge type={badgeType} label={value} />}</div>
              {timeRemaning && (
                <div>
                  {timeRemaning} <FormattedMessage id="quote.listing.quoteRemaining" defaultMessage="remaining" />
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      Header: getLabelWithTranslationFallback("Confirmation time", {
        translation: intl.formatMessage({
          id: "new-request.quote-confirmation.schedule.confirmationDate",
          defaultMessage: "Confirmation date",
        }),
        returnAsString: true,
        withoutInfo: true,
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      sortable: true,
      accessor: "confirmation_datetime",
      Cell: ({ value }) => {
        const time = moment(value).format("HH:mm");
        return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
      },
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.createdDate", defaultMessage: "Created date" }),
      sortable: true,
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "created",
      Cell: ({ value }) => {
        const time = moment(value).format("HH:mm");
        return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
      },
    },
    {
      Header: intl.formatMessage({ id: "new-request.packing-list.dangerousGoods", defaultMessage: "Dangerous goods" }),
      accessor: "dangerous_goods",
      sortable: false,
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      Cell: ({ value }) => <div>{value ? <CheckmarkCircle /> : "No"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.totalWeightKg", defaultMessage: "Total weight (kg)" }),
      accessor: "total_weight_kg",
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      Cell: ({ value }) => <div>{formatWeight(value, "kg")}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.totalWeightLbs", defaultMessage: "Total weight (lbs)" }),
      accessor: "total_weight_lbs",
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      Cell: ({ value }) => <div>{formatWeight(value, "lbs")}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.totalVolumeCbm", defaultMessage: "Total volume (cbm)" }),
      accessor: "total_volume_cbm",
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      Cell: ({ value }) => (
        <div>
          {value || value === 0 ? (
            <span>
              {roundTo(value, 4)}m<sup>3</sup>
            </span>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "tableHeaders.expectedPickupDateTime",
        defaultMessage: "Expected pickup date time",
      }),
      accessor: "pickup_date",
      group: intl.formatMessage({
        id: "columns.schedule",
        defaultMessage: "Schedule",
      }),
      sortable: true,
      Cell: ({ value }) => {
        const time = moment(value).format("HH:mm");
        return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
      },
    },
    {
      Header: intl.formatMessage({
        id: "tableHeaders.expectedDeliveryDateTime",
        defaultMessage: "Expected delivery date time",
      }),
      accessor: "stop1_date",
      group: intl.formatMessage({
        id: "columns.schedule",
        defaultMessage: "Schedule",
      }),
      sortable: true,
      Cell: ({ value }) => {
        const time = moment(value).format("HH:mm");
        if (value === "ASAP") return <div>{value}</div>;
        return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
      },
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.quotationsBooked", defaultMessage: "Quotations booked" }),
      accessor: "related_shipments_count",
      group: intl.formatMessage({
        id: "columns.schedule",
        defaultMessage: "Schedule",
      }),
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.exportClearance", defaultMessage: "Export clearance" }),
      accessor: "export_clearance",
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "quote-request-detail.transitClearance", defaultMessage: "Transit clearance" }),
      accessor: "transit_clearance",
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "quote-request-detail.importClearance", defaultMessage: "Import clearance" }),
      accessor: "import_clearance",
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({
        id: "new-request.service.serviceRequirement",
        defaultMessage: "Service requirement",
      }),
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      accessor: "service_requirement",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "new-request.service.incoterms", defaultMessage: "Incoterms" }),
      accessor: "incoterms",
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "general.insurance", defaultMessage: "Insurance" }),
      accessor: "insurance",
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      Cell: ({ value }) => (
        <div>
          {value
            ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
            : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
        </div>
      ),
    },
    {
      Header: intl.formatMessage({ id: "shipment-quotes.components.invitedProviders.won", defaultMessage: "Won" }),
      accessor: "is_won",
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      Cell: ({ value }) => <div>{value ? <CheckmarkCircle /> : "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "general.team", defaultMessage: "Team" }),
      id: "assigned_team",
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "assigned_team",
      sortable: true,
      Cell: ({ value }) => <div>{value?.name || "-"}</div>,
    },
  ];

  return columns;
};

export default getQuoteListingColumns;
